export type SearchFilters = {
  state?: string;
  metro?: string;
  sex?: string;
  age?: string;
  ethnicity?: string;
  sort?: string;
};

export function normalizeSearchFilters(filters: SearchFilters): Required<SearchFilters> {
  const state = filters.state ? String(filters.state).toUpperCase() : undefined;
  const metro = filters.metro ? String(filters.metro).toLowerCase() : undefined;
  const sex = filters.sex ? String(filters.sex).toLowerCase() : undefined;
  const age = filters.age ? String(filters.age).toLowerCase() : undefined;
  const ethnicity = filters.ethnicity ? String(filters.ethnicity).toLowerCase() : undefined;
  const sort = filters.sort && ['new', 'popular', 'nearby', 'relevance', 'best'].includes(String(filters.sort).toLowerCase())
    ? String(filters.sort).toLowerCase() : undefined;
  return { state, metro, sex, age, ethnicity, sort } as Required<SearchFilters>;
}

export function buildSearchCanonical(baseUrl: string, filters: SearchFilters): string {
  const base = (baseUrl || '').replace(/\/$/, '');
  const { state, metro, sex, age, ethnicity, sort } = normalizeSearchFilters(filters);
  const segments: string[] = ['/search'];
  if (state) segments.push(`state-${state}`);
  if (metro) segments.push(`metro-${metro}`);
  if (sex) segments.push(`sex-${sex}`);
  if (age) segments.push(`age-${age}`);
  if (ethnicity) segments.push(`ethnic-${ethnicity}`);
  if (sort) segments.push(`sort-${sort}`);
  return `${base}${segments.join('/')}`;
}

/**
 * Truncate meta description to optimal length for SERP display
 * Google typically shows 155-160 characters
 */
export function truncateDescription(description: string, maxLength: number = 155): string {
  if (!description) return '';
  const cleaned = description.trim().replace(/\s+/g, ' ');
  if (cleaned.length <= maxLength) return cleaned;

  // Try to break at last complete word before maxLength
  const truncated = cleaned.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > maxLength * 0.8) {
    // Break at word boundary if it's not too far back
    return truncated.slice(0, lastSpace) + '…';
  }

  return truncated + '…';
}

/**
 * Generate SEO-friendly title with proper length
 * Google displays ~60 characters on desktop, ~50 on mobile
 */
export function formatTitle(title: string, suffix: string = 'JuicyStarz', maxLength: number = 60): string {
  if (!title) return suffix;

  const fullTitle = `${title} · ${suffix}`;
  if (fullTitle.length <= maxLength) return fullTitle;

  // Truncate the title part, keep the suffix
  const availableLength = maxLength - suffix.length - 3; // 3 for ' · '
  if (availableLength < 10) return suffix; // Title too short, just use suffix

  const truncated = title.slice(0, availableLength).trim();
  const lastSpace = truncated.lastIndexOf(' ');

  if (lastSpace > availableLength * 0.8) {
    return `${truncated.slice(0, lastSpace)}… · ${suffix}`;
  }

  return `${truncated}… · ${suffix}`;
}

/**
 * Strip HTML tags and decode entities for plain text meta content
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

